import React, { useState, useEffect, useRef } from "react";

import { toast } from "react-toastify";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import { Stack } from "@mui/material";

import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === 'dark';

  return {
    root: {
      display: "flex",
      flexWrap: "wrap"
    },

    dialogPaper: {
      borderRadius: 14,
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: isDark
        ? "0 18px 60px rgba(0, 0, 0, 0.4)"
        : "0 18px 60px rgba(16, 24, 40, 0.18)",
      overflow: "hidden",
      backgroundColor: theme.palette.background.paper
    },

    dialogTitle: {
      padding: "16px 20px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      backgroundColor: theme.palette.background.paper
    },

    titleWrapper: {
      display: "flex",
      flexDirection: "column",
      gap: 2
    },

    title: {
      fontSize: 16,
      fontWeight: 600,
      fontFamily: "Inter, system-ui, -apple-system, BlinkMacSystemFont",
      color: theme.palette.text.primary
    },

    subtitle: {
      fontSize: 12.5,
      color: theme.palette.text.secondary
    },

    closeButton: {
      width: 36,
      height: 36,
      borderRadius: 10,
      border: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.secondary
    },

    dialogContent: {
      padding: 20,
      background: isDark
        ? "#0d1626"
        : "linear-gradient(180deg, rgba(249,250,251,0.6) 0%, rgba(255,255,255,1) 100%)",

      "& .MuiFormControl-root": {
        marginTop: 6,
        marginBottom: 6
      },

      "& .MuiInputLabel-outlined": {
        fontSize: 13,
        color: theme.palette.text.secondary
      },

      "& .MuiOutlinedInput-root": {
        borderRadius: 12,
        backgroundColor: isDark ? "#111b2e" : "#fff"
      },

      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: isDark ? "rgba(148, 163, 184, 0.3)" : "rgba(17, 24, 39, 0.14)"
      },

      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: isDark ? "rgba(148, 163, 184, 0.5)" : "rgba(17, 24, 39, 0.22)"
      },

      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: isDark ? "rgba(56, 189, 248, 0.65)" : "rgba(59, 130, 246, 0.65)"
      },

      "& .MuiFormHelperText-root": {
        marginLeft: 2
      }
    },

    dialogActions: {
      padding: "14px 20px",
      borderTop: `1px solid ${theme.palette.divider}`,
      gap: 10,
      backgroundColor: theme.palette.background.paper
    },

    primaryButton: {
      borderRadius: 12,
      textTransform: "none",
      boxShadow: "0 6px 18px rgba(59, 130, 246, 0.25)"
    },

    secondaryButton: {
      borderRadius: 12,
      textTransform: "none"
    }
  };
});

const FlowBuilderIntervalModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMounted = useRef(true);

  const [timerSec, setTimerSec] = useState(0);
  const [activeModal, setActiveModal] = useState(false);

  useEffect(() => {
    if (open === "edit") {
      setTimerSec(data.data.sec);
      setActiveModal(true);
    } else if (open === "create") {
      setTimerSec(0);
      setActiveModal(true);
    }
    return () => {
      isMounted.current = false;
    };
  }, [open, data]);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleSaveContact = () => {
    if (!timerSec || parseInt(timerSec) <= 0) {
      return toast.error("Adicione o valor de intervalo");
    }
    if (parseInt(timerSec) > 120) {
      return toast.error("Máximo de tempo atingido 120 segundos");
    }

    if (open === "edit") {
      onUpdate({
        ...data,
        data: { sec: timerSec }
      });
    } else if (open === "create") {
      onSave({
        sec: timerSec
      });
    }
    handleClose();
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={activeModal}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle className={classes.dialogTitle}>
          <div className={classes.titleWrapper}>
            <Typography className={classes.title}>
              {open === "create" ? "Adicionar intervalo ao fluxo" : "Editar intervalo"}
            </Typography>
            <Typography className={classes.subtitle}>
              Defina o tempo de espera em segundos
            </Typography>
          </div>

          <IconButton
            onClick={handleClose}
            className={classes.closeButton}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent className={classes.dialogContent} dividers>
          <Stack spacing={2}>
            <TextField
              label="Tempo em segundos"
              type="number"
              value={timerSec}
              onChange={e => setTimerSec(e.target.value)}
              variant="outlined"
              margin="dense"
              fullWidth
              inputProps={{ min: 0, max: 120 }}
            />
            <Typography
              style={{
                fontSize: 12,
                color: "#B45309",
                background: "rgba(251, 191, 36, 0.12)",
                border: "1px solid rgba(251, 191, 36, 0.35)",
                borderRadius: 8,
                padding: "6px 8px"
              }}
            >
              Valor maximo: 120 segundos
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={handleClose}
            variant="outlined"
            className={classes.secondaryButton}
          >
            {i18n.t("contactModal.buttons.cancel")}
          </Button>
          <Button
            onClick={handleSaveContact}
            color="primary"
            variant="contained"
            className={classes.primaryButton}
          >
            {open === "create" ? "Adicionar" : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FlowBuilderIntervalModal;
